import { createSignal, Show } from "solid-js";
import MetadataPicker, { type MetadataPickerType } from "../utilities/MetadataPicker";
import { splitMetadataFilterTokens } from "../../utils/metadataFilterTokens";
import "../../styles/metadata-picker.css";

/**
 * Resolve a settings-menu filter prop that may be a plain string or an accessor.
 * Shared by the settings modals that accept `filterYear` / `filterEvent` props.
 */
export const resolveStringAccessor = (value?: string | (() => string)): string =>
  typeof value === "function" ? value() : (value ?? "");

interface MetadataFilterFieldsProps {
  /** Local mirror accessors/setters owned by the parent modal (committed on its Apply). */
  yearValue: () => string;
  onYearInput: (value: string) => void;
  eventValue: () => string;
  onEventInput: (value: string) => void;
  /** Lowercased source names to scope the MetadataPicker's available values. */
  getSourceNames: () => string[];
}

/**
 * Year/Event metadata contains-filter fields (text input + "..." browse via MetadataPicker),
 * shared by the cheat-sheet settings modals. The parent owns the values and commit semantics;
 * this component only renders the two fields and the picker.
 */
export default function MetadataFilterFields(props: MetadataFilterFieldsProps) {
  const [metadataPickerKind, setMetadataPickerKind] = createSignal<MetadataPickerType | null>(null);

  return (
    <>
      <div class="pagesettings-modal-row2">
        {/* Year */}
        <div>
          <label class="block text-sm font-medium mb-2" style="color: var(--color-text-secondary);">Year</label>
          <div class="metadata-filter-field">
            <input
              type="text"
              value={props.yearValue()}
              onInput={(e) => props.onYearInput(e.currentTarget.value)}
              title="Year tokens — contains match (comma-separated)"
              placeholder="Example: 2026"
              class="metadata-filter-field__input"
            />
            <button
              type="button"
              class="metadata-filter-field__browse"
              onClick={() => setMetadataPickerKind("year")}
            >
              ...
            </button>
          </div>
        </div>
        {/* Event */}
        <div>
          <label class="block text-sm font-medium mb-2" style="color: var(--color-text-secondary);">Event</label>
          <div class="metadata-filter-field">
            <input
              type="text"
              value={props.eventValue()}
              onInput={(e) => props.onEventInput(e.currentTarget.value)}
              title="Event tokens — contains match (comma-separated)"
              placeholder="Example: PERTH"
              class="metadata-filter-field__input"
            />
            <button
              type="button"
              class="metadata-filter-field__browse"
              onClick={() => setMetadataPickerKind("event")}
            >
              ...
            </button>
          </div>
        </div>
      </div>

      <Show when={metadataPickerKind()}>
        {(kindAccessor) => {
          const type = kindAccessor();
          return (
            <MetadataPicker
              metadataType={type}
              initialSelected={
                type === "year"
                  ? splitMetadataFilterTokens(props.yearValue())
                  : splitMetadataFilterTokens(props.eventValue())
              }
              getSourceNames={props.getSourceNames}
              onSave={(values) => {
                const joined = values.join(",");
                if (type === "year") props.onYearInput(joined);
                else props.onEventInput(joined);
                setMetadataPickerKind(null);
              }}
              onCancel={() => setMetadataPickerKind(null)}
            />
          );
        }}
      </Show>
    </>
  );
}
